import { ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SectionHeading, SETTLE_SPRING } from "./SectionHeading";

/**
 * The 8 questions a section doesn't already answer. Unmounted keys stay in
 * home.json as the candidate pool: qSix → covered by EmailLine, qTwelve →
 * HowSteps step 1, qFourteen → Models, qSixteen → the Introductions section.
 *
 * There is deliberately NO Studio question ("can it build things?"). Studio
 * was pulled from the page entirely (operator decision, see launchStages.ts)
 * — do not add a Studio answer here as the back door that puts it back.
 *
 * Keep the visible list and the index.html FAQ JSON-LD in lockstep.
 */
const ITEMS = [
  ["qOne", "aOne"],
  ["qTwo", "aTwo"],
  ["qThree", "aThree"],
  ["qFour", "aFour"],
  ["qFive", "aFive"],
  ["qTen", "aTen"],
  ["qEleven", "aEleven"],
  ["qThirteen", "aThirteen"],
] as const;

export function FaqSection() {
  const { t } = useTranslation("home");

  return (
    <section id="faq" className="scroll-mt-32 md:scroll-mt-24 bg-home-cream-deep px-5 py-20 md:py-24">
      <div className="mx-auto max-w-2xl">
        <SectionHeading level="utility" kicker={t("faq.kicker")} title={t("faq.title")} />

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ ...SETTLE_SPRING, delay: 0.1 }}
          className="mt-12 divide-y divide-home-ink/8 rounded-[28px] bg-white/80 px-7 shadow-[0_20px_50px_-30px_rgba(33,31,28,0.4)]"
        >
          {ITEMS.map(([q, a]) => (
            <details key={q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-body text-[17px] font-semibold text-home-ink [&::-webkit-details-marker]:hidden">
                {t(`faq.${q}`)}
                <ChevronDown className="h-4 w-4 shrink-0 text-home-ink/65 transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <p className="mt-3 pr-8 font-body text-[15px] leading-relaxed text-home-ink/70">
                {t(`faq.${a}`)}
              </p>
            </details>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
